import { INPUT_COMPONENT_TYPE } from '@abiglobalhealth/go-types'
import { t } from 'i18next'
import { z } from 'zod'

import i18n from '@/locale/i18n'
import { getSlugTranslationKeys } from '@/locale/utils'

export const getZodSchema = ({
  type,
  id,
  minLength,
  maxLength,
  min,
  max,
  required,
  isDecimal,
  minDate,
  maxDate
}: {
  type: INPUT_COMPONENT_TYPE
  id: string
  minLength?: number
  maxLength?: number
  min?: number
  max?: number
  required?: boolean
  isDecimal?: boolean
  minDate?: string
  maxDate?: string
}) => {
  let zSchema

  const getValidationMessage = (base: string, options?: Record<string, any>): string => {
    const keys = [...getSlugTranslationKeys(`error.validation.${base}.${id}`), `error.validation.${base}`]

    for (const key of keys) {
      if (i18n.exists(key)) return t(key, options ?? {}) as string
    }

    return 'error.validation.default'
  }

  switch (type) {
    case INPUT_COMPONENT_TYPE.TEXT:
    case INPUT_COMPONENT_TYPE.TEXTAREA:
    case INPUT_COMPONENT_TYPE.EMAIL: {
      zSchema = z.string({
        error: 'error.validation.required'
      })

      if (type === INPUT_COMPONENT_TYPE.EMAIL) {
        zSchema = zSchema.email({ message: getValidationMessage('email') })
      }

      if (minLength != null) {
        zSchema = zSchema.min(minLength, {
          message: getValidationMessage('min_value', { min: minLength })
        })
      }
      if (maxLength != null) {
        zSchema = zSchema.max(maxLength, {
          message: getValidationMessage('max_value', { max: maxLength })
        })
      }
      if (required) zSchema = zSchema.min(1, { message: getValidationMessage('required') })

      break
    }

    case INPUT_COMPONENT_TYPE.NUMBER: {
      zSchema = z.number({
        error: 'error.validation.required'
      })

      if (min != null) {
        zSchema = zSchema.min(min, {
          message: getValidationMessage('min_value', { min })
        })
      }
      if (max != null) {
        zSchema = zSchema.max(max, {
          message: getValidationMessage('max_value', { max })
        })
      }
      if (!isDecimal) {
        zSchema = zSchema.int()
      }

      break
    }

    case INPUT_COMPONENT_TYPE.DATE: {
      zSchema = z
        .string({
          error: 'error.validation.required'
        })
        .refine(
          (value) => {
            const currentDate = new Date(value).getTime()

            if (minDate) {
              const minDateValue = new Date(minDate).getTime()
              if (currentDate < minDateValue) return false
            }

            return true
          },
          { message: getValidationMessage('min_value', { min: minDate }) }
        )
        .refine(
          (value) => {
            const currentDate = new Date(value).getTime()

            if (maxDate) {
              const maxDateValue = new Date(maxDate).getTime()
              if (currentDate > maxDateValue) return false
            }

            return true
          },
          { message: getValidationMessage('max_value', { max: maxDate }) }
        )
      break
    }

    case INPUT_COMPONENT_TYPE.CHECKBOX: {
      zSchema = z.boolean({
        error: 'error.validation.required'
      })
      break
    }

    case INPUT_COMPONENT_TYPE.PASSWORD: {
      zSchema = z.string({
        error: 'error.validation.required'
      })
      break
    }

    case INPUT_COMPONENT_TYPE.WEIGHT:
    case INPUT_COMPONENT_TYPE.HEIGHT: {
      zSchema = z
        .number({
          error: 'error.validation.required'
        })
        .min(0, {
          message: getValidationMessage('min_value', { min: 0 })
        })
      break
    }

    default: {
      console.error('No zod schema found for type', type)
      return z.any().optional()
    }
  }

  if (!required) return zSchema.optional()

  return zSchema
}
